<script setup lang="ts">
/**
 * One tile: a head with app, caption and actions, below it a whole Connect in an
 * iframe (`?tile=<id>`, chrome reduced by the shell) or the apps to pick from.
 * The frame's address is taken once: what the user does in it is the frame's own
 * navigation, reported back, and changing `src` would reload the tile.
 */
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Columns2 from '@lucide/vue/dist/esm/icons/columns-2.mjs'; import House from '@lucide/vue/dist/esm/icons/house.mjs'; import Maximize2 from '@lucide/vue/dist/esm/icons/maximize-2.mjs'; import Minimize2 from '@lucide/vue/dist/esm/icons/minimize-2.mjs'; import RotateCw from '@lucide/vue/dist/esm/icons/rotate-cw.mjs'; import Rows2 from '@lucide/vue/dist/esm/icons/rows-2.mjs'; import X from '@lucide/vue/dist/esm/icons/x.mjs';








import {
  NxButton,
  NxContextMenu,
  NxContextMenuItem,
  NxContextMenuSeparator,
  NxEmptyState,
  NxIconButton,
  NxSpinner,
  NxTile,
  NxTilePane,
} from "@blpde/nx-ui";
import { connectTileFrameOf } from "@blpde/connect";
import { apps, appForPath } from "@/integration";
import { routeLabel, tileCaption } from "./tileLabels";
import { useAccountApplications } from "@/applications";
import { useModuleAvailability } from "@/modules";
import { createTileFrameLoader, type TileFrameProbe } from "./tileFrame";
import { tileStartQueue } from "./tileStart";
import { MAX_TILES, type TileLeaf, type TileDrop } from "./tileTree";

const props = defineProps<{
  tile: TileLeaf;
  active: boolean;
  maximized: boolean;
  /** A drag runs over the page: the frame must not swallow the pointer. */
  frameInert: boolean;
  /** A tile is being moved: the drop zones show. */
  moving: boolean;
  /** This tile is being dragged. */
  lifted: boolean;
  /** Where a drop would land on this tile. */
  dropTarget: TileDrop | null;
  /** The only tile of its tab: shown as a plain page, without head and frame. */
  single: boolean;
  /** Whether a new tile fits to the right of this one. */
  canSplitRight: boolean;
  /** Whether a new tile fits below this one. */
  canSplitDown: boolean;
}>();

const emit = defineEmits<{
  pick: [route: string];
  home: [];
  reload: [];
  /** The frame's code runs (it hears what is sent to it), or a new frame starts. */
  running: [running: boolean];
  split: [side: "right" | "bottom"];
  maximize: [];
  close: [];
  activate: [];
  "drag-start": [event: PointerEvent];
}>();

const { t } = useI18n();
const { isEntitled } = useAccountApplications();
const { isAvailable } = useModuleAvailability();
const frame = ref<HTMLIFrameElement | null>(null);

const menu = ref<{ x: number; y: number } | null>(null);
const menuOpen = computed({
  get: () => !!menu.value,
  set: (open) => {
    if (!open) menu.value = null;
  },
});

const app = computed(() =>
  props.tile.route ? appForPath(props.tile.route) : undefined,
);
const appLabel = computed(
  () =>
    (props.tile.route && routeLabel(props.tile.route, t)) ||
    t("connect.tiles.empty"),
);
const pickableApps = computed(() =>
  apps.filter((a) => isEntitled(a.id) && isAvailable(a.moduleKey)),
);

function frameSrc(route: string): string {
  const query = new URLSearchParams({ tile: props.tile.id });
  // `?url=` picks the backend ahead of the stored one; a tile talks to the same one.
  const backend = new URLSearchParams(location.search).get("url");
  if (backend) query.set("url", backend);
  return `${location.pathname}?${query}#${route}`;
}

const src = ref("");
/** A new key is a new frame: the only start that also works from a browser error page. */
const frameKey = ref(0);
/** Where the frame was started; the tile may have been sent elsewhere since. */
let startedRoute: string | null = null;

function probeFrame(): TileFrameProbe {
  const element = frame.value;
  if (!element) return "not-yet";
  // No document to see: a browser error page, which counts as another origin.
  if (!element.contentDocument) return "unreachable";
  return connectTileFrameOf(element.contentWindow) ? "running" : "not-yet";
}

const loader = createTileFrameLoader({
  start: () => {
    emit("running", false);
    startedRoute = props.tile.route;
    if (startedRoute) src.value = frameSrc(startedRoute);
    frameKey.value += 1;
  },
  probe: probeFrame,
  onBooted: () => {
    emit("running", true);
    const route = props.tile.route;
    if (route && route !== startedRoute)
      frame.value?.contentWindow?.postMessage(
        { type: "connect.tile.navigate", path: route },
        location.origin,
      );
  },
  release: () => tileStartQueue.done(props.tile.id),
});
const { phase, slow } = loader;

// Frames start one at a time (`tileStart.ts`). A running frame keeps its address:
// it navigates by itself, and changing `src` would reload it.
function requestStart(): void {
  tileStartQueue.request(
    { tileId: props.tile.id, start: loader.begin },
    props.active,
  );
}

if (props.tile.route) requestStart();
watch(
  () => props.tile.route,
  (route) => {
    if (route && phase.value === "waiting" && !src.value) requestStart();
  },
);
onBeforeUnmount(() => {
  emit("running", false);
  loader.dispose();
  tileStartQueue.done(props.tile.id);
});

defineExpose({
  frameWindow: () => frame.value?.contentWindow ?? null,
  frameBooted: loader.booted,
  frameShown: loader.shown,
  frameFailed: loader.failed,
  frameGaveUp: () => phase.value === "failed",
  reloadFrame: () => {
    if (!props.tile.route) return;
    // It may still wait for its turn, and must not start twice.
    tileStartQueue.done(props.tile.id);
    loader.restart();
  },
});

const dropLabels = computed(() =>
  props.moving
    ? {
        left: t("connect.tiles.drop.left"),
        right: t("connect.tiles.drop.right"),
        top: t("connect.tiles.drop.top"),
        bottom: t("connect.tiles.drop.bottom"),
        swap: t("connect.tiles.drop.swap"),
      }
    : null,
);
</script>

<template>
  <NxTilePane
    :name="appLabel"
    :caption="tileCaption(tile)"
    :active="active"
    :lifted="lifted"
    :drop-labels="dropLabels"
    :drop-target="dropTarget"
    :plain="single"
    :data-tile-id="tile.id"
    data-testid="tile-pane"
    @pointerdown.capture="emit('activate')"
    @focusin="emit('activate')"
    @head-pointerdown="emit('drag-start', $event)"
    @head-menu="menu = { x: $event.clientX, y: $event.clientY }"
  >
    <template #icon>
      <component
        :is="app.icon"
        v-if="app"
        class="nxt-icon-xs"
        aria-hidden="true"
      />
    </template>
    <template #actions>
      <NxIconButton
        height="xs"
        :aria-label="t('connect.tiles.home')"
        :title="t('connect.tiles.home')"
        @click="emit('home')"
      >
        <House class="nxt-icon-sm" />
      </NxIconButton>
      <NxIconButton
        height="xs"
        :aria-label="t('connect.tiles.reload')"
        :title="t('connect.tiles.reload')"
        @click="emit('reload')"
      >
        <RotateCw class="nxt-icon-sm" />
      </NxIconButton>
      <NxIconButton
        height="xs"
        :aria-label="t('connect.tiles.splitRight')"
        :title="
          canSplitRight
            ? t('connect.tiles.splitRight')
            : t('connect.tiles.splitFull', { max: MAX_TILES })
        "
        :disabled="!canSplitRight"
        @click="emit('split', 'right')"
      >
        <Columns2 class="nxt-icon-sm" />
      </NxIconButton>
      <NxIconButton
        height="xs"
        :aria-label="t('connect.tiles.splitDown')"
        :title="
          canSplitDown
            ? t('connect.tiles.splitDown')
            : t('connect.tiles.splitFull', { max: MAX_TILES })
        "
        :disabled="!canSplitDown"
        @click="emit('split', 'bottom')"
      >
        <Rows2 class="nxt-icon-sm" />
      </NxIconButton>
      <NxIconButton
        height="xs"
        :aria-label="
          maximized ? t('connect.tiles.restore') : t('connect.tiles.maximize')
        "
        :title="
          maximized ? t('connect.tiles.restore') : t('connect.tiles.maximize')
        "
        @click="emit('maximize')"
      >
        <Minimize2 v-if="maximized" class="nxt-icon-sm" />
        <Maximize2 v-else class="nxt-icon-sm" />
      </NxIconButton>
      <NxIconButton
        height="xs"
        :aria-label="t('connect.tiles.close')"
        :title="t('connect.tiles.close')"
        @click="emit('close')"
      >
        <X class="nxt-icon-sm" />
      </NxIconButton>
    </template>
    <iframe
      v-if="src"
      :key="frameKey"
      ref="frame"
      class="cx-tile-frame"
      :class="{ 'is-inert': frameInert }"
      :src="src"
      :title="tileCaption(tile) || appLabel"
      @load="loader.documentLoaded"
    />
    <div v-if="tile.route && phase !== 'shown'" class="cx-tile-loading">
      <NxEmptyState
        v-if="phase === 'failed'"
        :title="t('connect.tiles.loadFailedTitle')"
        :description="t('connect.tiles.loadFailed')"
      >
        <NxButton @click="emit('reload')">
          {{ t("connect.tiles.reload") }}
        </NxButton>
      </NxEmptyState>
      <template v-else>
        <NxSpinner />
        <NxEmptyState
          v-if="slow"
          compact
          :title="t('connect.tiles.loadSlowTitle')"
          :description="t('connect.tiles.loadSlow')"
        >
          <NxButton @click="emit('reload')">
            {{ t("connect.tiles.reload") }}
          </NxButton>
        </NxEmptyState>
      </template>
    </div>
    <div v-else-if="!tile.route" class="cx-tile-pick">
      <p class="cx-tile-pick-heading">{{ t("connect.tiles.pick") }}</p>
      <div class="cx-tile-pick-list">
        <NxTile
          v-for="entry in pickableApps"
          :key="entry.id"
          layout="stacked"
          compact
          activatable
          :icon="entry.icon"
          :title="t(entry.labelKey)"
          :description="
            t(
              `connect.home.open${entry.id[0].toUpperCase()}${entry.id.slice(1)}`,
            )
          "
          @activate="emit('pick', entry.basePath)"
        />
      </div>
    </div>
    <NxContextMenu v-model:open="menuOpen" :x="menu?.x ?? 0" :y="menu?.y ?? 0">
      <NxContextMenuItem @select="emit('home')">
        {{ t("connect.tiles.home") }}
      </NxContextMenuItem>
      <NxContextMenuItem @select="emit('reload')">
        {{ t("connect.tiles.reload") }}
      </NxContextMenuItem>
      <NxContextMenuSeparator />
      <NxContextMenuItem
        :disabled="!canSplitRight"
        :description="
          canSplitRight
            ? undefined
            : t('connect.tiles.splitFull', { max: MAX_TILES })
        "
        @select="emit('split', 'right')"
      >
        {{ t("connect.tiles.splitRight") }}
      </NxContextMenuItem>
      <NxContextMenuItem
        :disabled="!canSplitDown"
        :description="
          canSplitDown
            ? undefined
            : t('connect.tiles.splitFull', { max: MAX_TILES })
        "
        @select="emit('split', 'bottom')"
      >
        {{ t("connect.tiles.splitDown") }}
      </NxContextMenuItem>
      <NxContextMenuSeparator />
      <NxContextMenuItem @select="emit('maximize')">
        {{
          maximized ? t("connect.tiles.restore") : t("connect.tiles.maximize")
        }}
      </NxContextMenuItem>
      <NxContextMenuSeparator />
      <NxContextMenuItem @select="emit('close')">
        {{ t("connect.tiles.close") }}
      </NxContextMenuItem>
    </NxContextMenu>
  </NxTilePane>
</template>

<style scoped>
.cx-tile-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}
.cx-tile-frame.is-inert {
  pointer-events: none;
}
.cx-tile-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-md);
  /* A half-loaded page or a browser error page stays out of sight. */
  background: var(--nxt-surface-bg);
}
.cx-tile-pick {
  height: 100%;
  padding: var(--nxt-space-md);
  overflow: auto;
}
.cx-tile-pick-heading {
  margin: 0 0 var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}
.cx-tile-pick-list {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(calc(var(--nxt-size-unit) * 10), 1fr)
  );
  gap: var(--nxt-space-sm);
}
</style>
